import { Offcanvas, Form, Button } from "react-bootstrap";
import { REACT_SELECT_STYLES } from "configurations/Constants";
import {
  LABEL_SELECT_BUSINESS,
  LABEL_SELECT_FARMS,
  LABEL_SELECT_BUSINESS_AND_FARMS_TO_DISPLAY,
  LABEL_SITES_WITH_NO_CROPS,
  LABEL_CHEMICAL_FREE_CROP_SITES,
} from "configurations/MessageConstants";
import Select from "react-select";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import FeatureConstructionTooltip from "components/common/FeatureConstructionTooltip";
import {
  setSelectedBusiness,
  setSelectedFarms,
  setSiteWithNoCrops,
  setChemicalFreeCropSites,
  setIsFilterApplied,
  resetSiteDrawerFilters,
} from "reduxStore/slices/siteFilterSlice";

const SiteFilter = ({ show, handleClose }) => {
  const {
    selectedBusiness,
    selectedFarms,
    siteWithNoCrops,
    chemicalFreeCropSites,
    isFilterApplied,
  } = useSelector((state) => state.siteFilter);
  const dispatch = useDispatch();
  const FARMS_BUSINESSES = useSelector((state) => state.global.farmsBusinesses);
  const BUSINESSES = useSelector((state) => state.global.businesses).map(
    (business) => ({
      value: business,
      label: business,
    })
  );
  const [farms, setFarms] = useState([]);
  const [filterSelectedBusiness, setFilterSelectedBusiness] = useState([]);
  const [filterSelectedFarms, setFilterSelectedFarms] = useState([]);
  const [filterSiteWithNoCrops, setFilterSiteWithNoCrops] = useState(false);
  const [filterChemicalFreeCropSites, setFilterChemicalFreeCropSites] =
    useState(false);

  useEffect(() => {
    setFarms(
      FARMS_BUSINESSES.map((entity) => ({
        value: entity.farmName,
        label: entity.farmName,
      }))
    );
  }, [FARMS_BUSINESSES]);

  useEffect(() => {
    if (show) {
      setFilterSelectedBusiness(selectedBusiness);
      setFilterSelectedFarms(selectedFarms);
      setFilterSiteWithNoCrops(siteWithNoCrops);
      setFilterChemicalFreeCropSites(chemicalFreeCropSites);
    }
  }, [
    show,
    selectedBusiness,
    selectedFarms,
    siteWithNoCrops,
    chemicalFreeCropSites,
  ]);

  const handleClear = () => {
    setFilterSelectedBusiness([]);
    setFilterSiteWithNoCrops(false);
    setFilterChemicalFreeCropSites(false);
    setFilterSelectedFarms([]);
  };

  const handleBusinessChange = (businesses) => {
    setFilterSelectedBusiness(businesses);
    if (!businesses || businesses.length === 0) {
      setFarms(
        FARMS_BUSINESSES.map((entity) => ({
          value: entity.farmName,
          label: entity.farmName,
        }))
      );
      setFilterSelectedFarms([]);
      return;
    }
    const selectedBusinessNames = businesses.map((business) => business.value);
    const filteredFarms = FARMS_BUSINESSES.filter((entity) =>
      selectedBusinessNames.includes(entity.businessName)
    );
    setFarms(
      filteredFarms.map((entity) => ({
        value: entity.farmName,
        label: entity.farmName,
      }))
    );
    setFilterSelectedFarms([]);
  };

  const handleFarmChange = (farms) => {
    setFilterSelectedFarms(farms);
  };

  const handleApply = () => {
    dispatch(setSelectedBusiness(filterSelectedBusiness));
    dispatch(setSelectedFarms(filterSelectedFarms));
    dispatch(setSiteWithNoCrops(filterSiteWithNoCrops));
    dispatch(setChemicalFreeCropSites(filterChemicalFreeCropSites));
    const hasFilters =
      filterSelectedBusiness.length > 0 ||
      filterSelectedFarms.length > 0 ||
      filterSiteWithNoCrops ||
      filterChemicalFreeCropSites;
    dispatch(setIsFilterApplied(hasFilters));
    handleClose();
  };
  const handleReset = () => {
    if (!isFilterApplied) return;
    handleClear();
    dispatch(resetSiteDrawerFilters());
    handleClose();
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      aria-labelledby="sites-filter"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title
          id="sites-filter"
          className="w-100 d-flex align-items-center justify-content-between"
        >
          <span>Sites Filter</span>
          <Button
            variant="link"
            onClick={handleReset}
            className="filter-reset-button"
            aria-label="Reset Filters"
          >
            <span className="filter-reset-button-text">Reset</span>
            <i className="fa fa-refresh"></i>
          </Button>
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <div className="d-flex gap-3 mb-2 align-items-center">
          <small>{LABEL_SITES_WITH_NO_CROPS}</small>
          <FeatureConstructionTooltip>
            <Form.Check
              type="switch"
              id="custom-switch"
              checked={filterSiteWithNoCrops}
              onChange={() => setFilterSiteWithNoCrops(!filterSiteWithNoCrops)}
              disabled={true}
              aria-label={`Toggle ${LABEL_SITES_WITH_NO_CROPS}`}
            />
          </FeatureConstructionTooltip>
        </div>
        <div className="d-flex gap-3 mb-4 align-items-center">
          <small>{LABEL_CHEMICAL_FREE_CROP_SITES}</small>
          <FeatureConstructionTooltip>
            <Form.Check
              type="switch"
              id="custom-switch-2"
              checked={filterChemicalFreeCropSites}
              onChange={() =>
                setFilterChemicalFreeCropSites(!filterChemicalFreeCropSites)
              }
              disabled={true}
              aria-label={`Toggle ${LABEL_CHEMICAL_FREE_CROP_SITES}`}
            />
          </FeatureConstructionTooltip>
        </div>
        <div className="mb-3">
          <small>{LABEL_SELECT_BUSINESS_AND_FARMS_TO_DISPLAY}</small>
        </div>
        <Form.Group className="mb-3">
          <Form.Label>Business</Form.Label>
          <Select
            isMulti
            value={filterSelectedBusiness}
            onChange={handleBusinessChange}
            options={BUSINESSES}
            placeholder={LABEL_SELECT_BUSINESS}
            styles={REACT_SELECT_STYLES}
            aria-label="Select Business"
          />
        </Form.Group>
        <Form.Group className="mb-4">
          <Form.Label>Farms</Form.Label>
          <Select
            isMulti
            value={filterSelectedFarms}
            onChange={handleFarmChange}
            options={farms}
            placeholder={LABEL_SELECT_FARMS}
            styles={REACT_SELECT_STYLES}
            aria-label="Select Farms"
          />
        </Form.Group>
        <div className="d-flex justify-content-end gap-2">
          <Button
            variant="outline-secondary"
            onClick={handleClear}
            aria-label="Clear Filters"
          >
            Clear
          </Button>
          <Button
            variant="primary"
            onClick={handleApply}
            aria-label="Apply Filters"
          >
            Apply
          </Button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default SiteFilter;
